/**
 * Adaptive Test Engine for Kazakan Cognitive Test
 * Manages item selection, difficulty adaptation, domain balancing, and session progression
 */

import { DOMAINS, ALL_TEMPLATES, generateQuestion } from './question-bank.js';

export class AdaptiveTestSession {
  constructor(prng, totalQuestions = 28) {
    this.prng = prng;
    this.totalQuestions = totalQuestions;
    this.questionsPerDomain = Math.floor(totalQuestions / DOMAINS.length); // 4

    // Domain progression state
    this.domainState = {};
    DOMAINS.forEach(d => {
      this.domainState[d.id] = {
        currentDifficulty: 2, // Start at medium
        usedTemplateIds: new Set(),
        history: [], // { isCorrect, difficulty, timeMs }
        questionsServed: 0
      };
    });

    // Build the interleaved domain schedule:
    // Cycle 0: [D0, D1, D2, D3, D4, D5, D6]
    // Cycle 1: [D0, D1, D2, D3, D4, D5, D6] ... for 4 cycles
    this.schedule = [];
    for (let cycle = 0; cycle < this.questionsPerDomain; cycle++) {
      // Slightly shuffle domains per cycle for engaging variety while keeping all 7 present
      const cycleDomains = this.prng.shuffle([...DOMAINS]);
      cycleDomains.forEach(d => {
        this.schedule.push(d.id);
      });
    }

    this.currentIndex = 0;
    this.sessionRecords = []; // { question, userSelectedIndex, isCorrect, timeMs }
    this.reviewItems = null;
  }

  /**
   * Generates the next question according to adaptive schedule
   */
  getNextQuestion() {
    if (this.currentIndex >= this.schedule.length) {
      return null; // Test finished
    }

    const domainId = this.schedule[this.currentIndex];
    const dState = this.domainState[domainId];

    // Find available templates for this domain that haven't been used yet
    const domainTemplates = ALL_TEMPLATES.filter(t => t.domain === domainId);
    let unusedTemplates = domainTemplates.filter(t => !dState.usedTemplateIds.has(t.id));

    if (unusedTemplates.length === 0) {
      // If exhausted, reuse from domain
      unusedTemplates = domainTemplates;
    }

    const chosenTemplate = this.prng.pick(unusedTemplates);
    dState.usedTemplateIds.add(chosenTemplate.id);

    // Generate question at current adaptive difficulty
    const questionItem = generateQuestion(chosenTemplate, this.prng, dState.currentDifficulty);
    questionItem.sessionNumber = this.currentIndex + 1;
    questionItem.totalTarget = this.totalQuestions;
    questionItem.domainId = domainId;

    return questionItem;
  }

  /**
   * Records user response and adapts difficulty for future items in this domain
   */
  recordResponse(questionItem, selectedIndex, timeMs) {
    const isCorrect = selectedIndex === questionItem.correctIndex;
    const domainId = questionItem.domainId;
    const dState = this.domainState[domainId];

    dState.history.push({
      isCorrect,
      difficulty: questionItem.difficulty,
      timeMs
    });
    dState.questionsServed++;

    this.sessionRecords.push({
      question: questionItem,
      selectedIndex,
      isCorrect,
      timeMs
    });

    // Adaptive rule:
    // If correct and difficulty < 3 -> increase to 3
    // If incorrect and difficulty > 1 -> decrease to 1
    if (isCorrect && dState.currentDifficulty < 3) {
      dState.currentDifficulty = Math.min(3, dState.currentDifficulty + 1);
    } else if (!isCorrect && dState.currentDifficulty > 1) {
      dState.currentDifficulty = Math.max(1, dState.currentDifficulty - 1);
    }

    this.currentIndex++;
    return { isCorrect, nextIndex: this.currentIndex };
  }

  isFinished() {
    return this.currentIndex >= this.totalQuestions;
  }

  getRecords() {
    return this.sessionRecords;
  }

  /**
   * Retrieves all items for the solutions and logical analysis review.
   * If the test was ended early before 28 questions (even at question 1),
   * generates the remaining questions in the schedule so the user can study
   * all 28 question solutions and comprehensive logical explanations.
   */
  getAllReviewItems(pendingQuestion = null) {
    if (this.reviewItems && this.reviewItems.length === this.totalQuestions) {
      return this.reviewItems;
    }

    const items = [...this.sessionRecords];
    const answeredTemplateIds = new Set(this.sessionRecords.map(r => r.question?.templateId));

    if (pendingQuestion && !answeredTemplateIds.has(pendingQuestion.templateId)) {
      items.push({
        question: pendingQuestion,
        selectedIndex: null,
        isCorrect: false,
        isUnanswered: true,
        timeMs: 0
      });
      this.currentIndex++;
    }

    while (this.currentIndex < this.schedule.length && items.length < this.totalQuestions) {
      const q = this.getNextQuestion();
      if (!q) break;
      items.push({
        question: q,
        selectedIndex: null,
        isCorrect: false,
        isUnanswered: true,
        timeMs: 0
      });
      this.currentIndex++;
    }

    // Ensure sessionNumber is consistent 1..28
    items.forEach((item, idx) => {
      if (item.question) {
        item.question.sessionNumber = idx + 1;
        item.question.totalTarget = this.totalQuestions;
      }
    });

    this.reviewItems = items;
    return this.reviewItems;
  }
}
